import { listDrives } from './drives.js';
import { createDocumentByTenant } from './documents.js';
import { extractDocumentDescription, detectContentFormat } from '../utils/contentUtils.js';

/** CT-5305: `type` must reflect the actual stored content, not always
 * `text/plain` — the chat message content is frequently HTML (TipTap output)
 * or markdown, and mislabeling it as plain text is what led downstream
 * consumers of this vault document to treat rich content as flat text. */
const MIME_BY_CONTENT_FORMAT = {
  html: 'text/html',
  markdown: 'text/markdown',
  text: 'text/plain',
};

/** Same rationale as drive upload: tenant document create can exceed default 10s client timeout. */
const DOCUMENT_CREATE_TIMEOUT_MS = 120_000;

const LIST_DRIVES_PARAMS = {
  scope: 'MANAGE',
  meta: {
    page: 1,
    page_size: 10,
    order_by: 'created_at',
    order_direction: 'DESC',
    filters: {},
  },
};

function parseDrivesResponse(data) {
  if (Array.isArray(data?.body)) return data.body;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.body?.data)) return data.body.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function getFirstVaultUuid(items) {
  const first = items[0];
  return first?.vaultUuid ?? first?.vault_uuid ?? first?.id ?? first?.uuid ?? null;
}

/**
 * Persists plain-text document content to the first MANAGE-scope vault (same strategy as chat “create from message”).
 *
 * @param {import('axios').AxiosInstance} apiClient
 * @param {{ title?: string, content?: string, metadataSource?: string }} params
 * @returns {Promise<{ ok: true, documentUuid?: string } | { ok: false, error: string }>}
 */
export async function saveDocumentContentToDefaultVault(apiClient, { title, content, metadataSource }) {
  if (!apiClient) {
    return { ok: false, error: 'Missing API client' };
  }
  const docTitle = title ?? 'Document';
  const docContent = typeof content === 'string' ? content : String(content ?? '');
  const description = extractDocumentDescription(docContent);

  try {
    const drivesResp = await listDrives(apiClient, LIST_DRIVES_PARAMS);
    const items = parseDrivesResponse(drivesResp?.data ?? drivesResp ?? {});
    const vaultUuid = getFirstVaultUuid(items);

    if (!vaultUuid) {
      return { ok: false, error: 'No workspace vault available.' };
    }

    const createBody = {
      name: docTitle,
      description,
      type: MIME_BY_CONTENT_FORMAT[detectContentFormat(docContent)] ?? 'text/plain',
      url: '',
      vaultUuid,
      folderUuid: null,
      metadata: {
        source: metadataSource ?? 'chat_document_panel',
        version: '1.0',
        content: docContent,
      },
    };

    const response = await createDocumentByTenant(apiClient, createBody, {
      timeout: DOCUMENT_CREATE_TIMEOUT_MS,
    });
    const data = response?.data || response || {};
    const statusCode = data.status_code ?? data.statusCode;

    if (statusCode != null && (statusCode < 200 || statusCode >= 300)) {
      const msg = data.msgCode?.[0]?.description || 'Failed to create document.';
      return { ok: false, error: msg };
    }
    const body = data.body || data;
    const documentUuid =
      body?.document?.document_uuid ?? body?.document?.documentUuid ?? body?.document_uuid;
    return { ok: true, documentUuid: documentUuid ?? undefined };
  } catch (err) {
    const msg =
      err?.response?.data?.msgCode?.[0]?.description || err?.message || 'Failed to save document.';
    return { ok: false, error: msg };
  }
}
